'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';


interface FormData {
  fullName: string;
  email: string;
  company: string;
  role: string;
  phone: string;
  projectBudget: string;
  projectTimeline: string;
  verticals: string[];
  platforms: string[];
  message: string;
  communicationPreference: string;
}

const ContactForm = () => {
  const router = useRouter();
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    company: '',
    role: '',
    phone: '',
    projectBudget: '',
    projectTimeline: '',
    verticals: [],
    platforms: [],
    message: '',
    communicationPreference: 'email'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Clear any error state on component mount
  useEffect(() => {
    setErrorMessage('');
    setIsSubmitting(false);
  }, []);

  const verticalOptions = [
    'Finance & Banking',
    'Healthcare & Pharmaceuticals',
    'Gambling & Gaming',
    'Cryptocurrency & Blockchain',
    'Adult Content',
    'Political Advertising',
    'E-commerce',
    'SaaS & Technology'
  ];

  const platformOptions = [
    'Meta (Facebook/Instagram)',
    'Google Ads',
    'TikTok',
    'LinkedIn',
    'Twitter/X',
    'Snapchat',
    'Pinterest',
    'Multiple Platforms'
  ];

  const budgetRanges = [
    'Under $10,000/month',
    '$10,000 - $50,000/month',
    '$50,000 - $100,000/month',
    '$100,000 - $500,000/month',
    'Over $500,000/month'
  ];

  const timelineOptions = [
    'Immediate (Within 2 weeks)',
    'Short-term (1-3 months)',
    'Medium-term (3-6 months)',
    'Long-term (6+ months)',
    'Exploratory'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (category: 'verticals' | 'platforms', value: string) => {
    setFormData(prev => ({
      ...prev,
      [category]: prev[category].includes(value)
        ? prev[category].filter(item => item !== value)
        : [...prev[category], value]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');
    
    try {
      const timestamp = Date.now();
      console.log(`[ContactForm] Submitting at ${new Date().toISOString()}:`, { 
        email: formData.email, 
        company: formData.company 
      });
      
      const response = await fetch(`/api/contact?_t=${timestamp}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();
      const apiVersion = response.headers.get('X-API-Version');
      
      console.log('[ContactForm] Response received:', { 
        success: result.success, 
        status: response.status,
        apiVersion,
        timestamp: new Date().toISOString()
      });

      if (!response.ok || !result.success) {
        const errorMsg = result.message || 'Failed to submit inquiry. Please try again.';
        const detailedError = result.error ? ` (${result.error})` : '';
        const versionInfo = apiVersion ? ` [API: ${apiVersion}]` : '';
        throw new Error(`${errorMsg}${detailedError}${versionInfo}`);
      }

      setSubmitSuccess(true);
      console.log('[ContactForm] Submission successful:', result.inquiryId);
      router.push('/contact/success');
      
    } catch (error) {
      console.error('[ContactForm] Submission error:', error);
      setIsSubmitting(false);
      const msg = error instanceof Error ? error.message : 'Failed to submit inquiry. Please try again or contact us at collab@dannyclans.com';
      setErrorMessage(msg);
    }
  };

  if (submitSuccess) {
    return null;
  }

  return (
    <form onSubmit={handleSubmit} className="bg-card border border-border rounded-sm p-8 space-y-8">
      {errorMessage && (
        <div className="bg-error bg-opacity-10 border border-error rounded-sm p-4 text-error">
          <p className="font-body text-sm">{errorMessage}</p>
          <p className="font-body text-xs mt-2">If the issue persists, contact us at collab@dannyclans.com</p>
        </div>
      )}

      {/* Basic Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="fullName" className="block font-headline font-semibold text-sm text-primary mb-2">
            Full Name *
          </label>
          <input
            type="text"
            id="fullName"
            name="fullName"
            value={formData.fullName}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-3 border border-border rounded-sm bg-background text-primary font-body text-base focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
            placeholder="John Smith"
          />
        </div>

        <div>
          <label htmlFor="email" className="block font-headline font-semibold text-sm text-primary mb-2">
            Email Address *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-3 border border-border rounded-sm bg-background text-primary font-body text-base focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
            placeholder="john@company.com"
          />
        </div>

        <div>
          <label htmlFor="company" className="block font-headline font-semibold text-sm text-primary mb-2">
            Company *
          </label>
          <input
            type="text"
            id="company"
            name="company"
            value={formData.company}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-3 border border-border rounded-sm bg-background text-primary font-body text-base focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
            placeholder="Your Company Name"
          />
        </div>

        <div>
          <label htmlFor="role" className="block font-headline font-semibold text-sm text-primary mb-2">
            Your Role *
          </label>
          <input
            type="text"
            id="role"
            name="role"
            value={formData.role}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-3 border border-border rounded-sm bg-background text-primary font-body text-base focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
            placeholder="Marketing Director"
          />
        </div>

        <div>
          <label htmlFor="phone" className="block font-headline font-semibold text-sm text-primary mb-2">
            Phone Number
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-border rounded-sm bg-background text-primary font-body text-base focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
            placeholder="+1 (555) 000-0000"
          />
        </div>

        <div>
          <label htmlFor="communicationPreference" className="block font-headline font-semibold text-sm text-primary mb-2">
            Preferred Contact Method
          </label>
          <select
            id="communicationPreference"
            name="communicationPreference"
            value={formData.communicationPreference}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-border rounded-sm bg-background text-primary font-body text-base focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
          >
            <option value="email">Email</option>
            <option value="phone">Phone</option>
            <option value="both">Both</option>
          </select>
        </div>
      </div>

      {/* Project Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="projectBudget" className="block font-headline font-semibold text-sm text-primary mb-2">
            Project Budget
          </label>
          <select
            id="projectBudget"
            name="projectBudget"
            value={formData.projectBudget}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-border rounded-sm bg-background text-primary font-body text-base focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
          >
            <option value="">Select budget range</option>
            {budgetRanges.map(range => (
              <option key={range} value={range}>{range}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="projectTimeline" className="block font-headline font-semibold text-sm text-primary mb-2">
            Project Timeline
          </label>
          <select
            id="projectTimeline"
            name="projectTimeline"
            value={formData.projectTimeline}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-border rounded-sm bg-background text-primary font-body text-base focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
          >
            <option value="">Select timeline</option>
            {timelineOptions.map(timeline => (
              <option key={timeline} value={timeline}>{timeline}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Verticals */}
      <div>
        <label className="block font-headline font-semibold text-sm text-primary mb-3">
          Industry Verticals
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {verticalOptions.map(vertical => (
            <label key={vertical} className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.verticals.includes(vertical)}
                onChange={() => handleCheckboxChange('verticals', vertical)}
                className="w-4 h-4 text-accent border-border rounded focus:ring-2 focus:ring-accent"
              />
              <span className="font-body text-sm text-primary">{vertical}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Platforms */}
      <div>
        <label className="block font-headline font-semibold text-sm text-primary mb-3">
          Advertising Platforms
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {platformOptions.map(platform => (
            <label key={platform} className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.platforms.includes(platform)}
                onChange={() => handleCheckboxChange('platforms', platform)}
                className="w-4 h-4 text-accent border-border rounded focus:ring-2 focus:ring-accent"
              />
              <span className="font-body text-sm text-primary">{platform}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Message */}
      <div>
        <label htmlFor="message" className="block font-headline font-semibold text-sm text-primary mb-2">
          Message *
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleInputChange}
          required
          rows={6}
          className="w-full px-4 py-3 border border-border rounded-sm bg-background text-primary font-body text-base focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all resize-none"
          placeholder="Tell us about your project requirements..."
        />
      </div>

      {/* Submit Button */}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-8 py-3 bg-accent text-white font-headline font-semibold rounded-sm hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
        </button>
      </div>

      <p className="text-xs text-text-secondary font-body text-center">
        * Required fields
      </p>
    </form>
  );
};

export default ContactForm;